import axios from 'axios';
import { getBackendHost } from '../utils/backendHost';

const BROWSER_CALL_TOKEN_TIMEOUT_MS = 8000;

async function requestBrowserCallToken(path = '/v2/onboarding/browser-call-token') {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), BROWSER_CALL_TOKEN_TIMEOUT_MS);
  try {
    const response = await fetch(`${getBackendHost()}${path}`, {
      method: 'POST',
      headers: {
        ...authConfig().headers,
        'Content-Type': 'application/json',
      },
      body: '{}',
      signal: controller.signal,
    });
    const payload = await response.json();
    if (!response.ok) {
      const error = new Error(payload?.error?.message || 'The browser call could not be prepared.');
      error.response = { status: response.status, data: payload };
      throw error;
    }
    return payload;
  } finally {
    clearTimeout(timeoutId);
  }
}

const authConfig = () => {
  const token = localStorage.getItem('jwt_authorization');
  return { headers: { Authorization: `Bearer ${token}` } };
};

export function getAdminBrowserCallToken() {
  return requestBrowserCallToken('/v2/admin/browser-call-token');
}

export async function getOnboarding() {
  const response = await axios.get(`${getBackendHost()}/v2/onboarding`, authConfig());
  return response.data.onboarding;
}

export async function dismissOnboardingSetupPlan() {
  const response = await axios.post(
    `${getBackendHost()}/v2/onboarding/setup-plan/dismiss`,
    {},
    authConfig(),
  );
  return response.data.onboarding;
}

export async function deferOnboardingTeamInvitation() {
  const response = await axios.post(
    `${getBackendHost()}/v2/onboarding/setup-plan/team-invitation/defer`,
    {},
    authConfig(),
  );
  return response.data.onboarding;
}

export async function saveOnboardingStep(stepKey, payload) {
  const response = await axios.patch(
    `${getBackendHost()}/v2/onboarding/steps/${stepKey}`,
    payload,
    authConfig(),
  );
  return response.data.onboarding;
}

export async function importOnboardingDocuments(files) {
  const form = new FormData();
  files.forEach(file => form.append('documents', file));
  const response = await axios.post(
    `${getBackendHost()}/v2/onboarding/imports/documents`,
    form,
    authConfig(),
  );
  return response.data.onboarding;
}

export async function importOnboardingWebsite(website) {
  const response = await axios.post(
    `${getBackendHost()}/v2/onboarding/imports/website`,
    { website },
    authConfig(),
  );
  return response.data.onboarding;
}

export async function getVoiceOptions() {
  const response = await axios.get(`${getBackendHost()}/voice-list`);
  return Array.isArray(response.data) ? response.data : [];
}

export async function previewVoice(voiceId, text) {
  const token = localStorage.getItem('jwt_authorization');
  const response = await axios.post(`${getBackendHost()}/preview-voice`, {
    voice_id: String(voiceId),
    text,
    jwt_token: token,
  });
  return response.data.preview_url;
}

export async function getOnboardingBrowserCallToken() {
  try {
    return await requestBrowserCallToken();
  } catch (error) {
    const retryable = !error?.response || error?.name === 'AbortError';
    if (!retryable) throw error;
    await new Promise(resolve => setTimeout(resolve, 400));
    return requestBrowserCallToken();
  }
}
